import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card, ToastContainer, ToastProvider } from '@forge/common';
import { useTranslation } from 'react-i18next';
import { I18nextProvider } from 'react-i18next';
import i18next from '../../../i18next';
import './activity.scss';
import { ActivityAppDispatch, useActivitySelector } from '../Redux/activityStore';
import {
  fetchActivity,
  setColumnConfig,
  setFilters,
  resetFilters,
  setPage,
  setPageSize,
  setSort,
  setSelectedItem,
  clearSelectedItem,
  resetActivity,
} from '../Redux/activitySlice';
import {
  selectActivityItems,
  selectActivityPagination,
  selectActivitySort,
  selectActivityFilters,
  selectActivityIsLoading,
  selectActivityError,
  selectHasLoadedOnce,
  selectSelectedActivityItem,
  selectColumnConfig,
} from '../Redux/activitySelectors';
import { IActivityItem, IColumnConfig, SortBy, SortOrder, WorkflowStatus } from '../Redux/activityModels';
import { IFilterSelections } from './activityFilter/ActivityFilterModal';
import ActivityToolbar from './activityToolbar/ActivityToolbar';
import ActivityTable from './activityTable/ActivityTable';
import ActivityDetailModal from './activityDetailModal/ActivityDetailModal';
import ActivityErrorState from './shared/ActivityErrorState';
import ActivityToastBridge from './activityToastBridge/ActivityToastBridge';

const ACTIVE_REFRESH_INTERVAL_MS = 60_000;   // fast poll while items are in-flight
const IDLE_REFRESH_INTERVAL_MS   = 300_000;  // slow poll when everything settled

interface ActivityTabContainerProps {
  selectedOrganizationId?: string;
  selectedOrganizationIds?: string[];
  siOrgId?: string;
  activityTableTitle?: string;
  isLoadingAccessibleCustomers?: boolean;
  isSIFlow?: boolean;
  selectedCustomerId?: string;
  selectedSiteId?: string;
}

const ActivityTabContainer: React.FC<ActivityTabContainerProps> = ({
  selectedOrganizationId,
  selectedOrganizationIds,
  siOrgId,
  activityTableTitle,
  isLoadingAccessibleCustomers = false,
  isSIFlow,
  selectedCustomerId,
  selectedSiteId,
}) => {
  const dispatch = useDispatch<ActivityAppDispatch>();
  // Detect flow type: new flow has selectedOrganizationIds array, old flow has only selectedOrganizationId
  const isNewFlow = isSIFlow !== undefined;
  // Keep IDs stable across renders to avoid re-triggering fetch effects.
  const organizationCustomerIds = useMemo(
    () => selectedOrganizationIds ?? (selectedOrganizationId ? [selectedOrganizationId] : []),
    [selectedOrganizationIds, selectedOrganizationId],
  );
  // If explicit IDs are provided by parent context, use them for query payload.
  const customerIds = useMemo(
    () => (selectedCustomerId ? [selectedCustomerId] : organizationCustomerIds),
    [selectedCustomerId, organizationCustomerIds],
  );
  const filterCustomerIds = useMemo(() => {
    if (!isSIFlow || !siOrgId) {
      return customerIds;
    }
    return customerIds.filter(customerId => customerId !== siOrgId);
  }, [customerIds, isSIFlow, siOrgId]);
  
  const requestSiteIds = useMemo(
    () => (selectedSiteId ? [selectedSiteId] : undefined),
    [selectedSiteId],
  );
  const customerId = selectedCustomerId ?? selectedOrganizationId ?? customerIds[0] ?? '';
  const { t } = useTranslation();

  const items = useActivitySelector(selectActivityItems);
  const pagination = useActivitySelector(selectActivityPagination);
  const { currentPage, pageSize } = pagination;
  const sort = useActivitySelector(selectActivitySort);
  const filters = useActivitySelector(selectActivityFilters);
  const { statuses, activity: activityFilters, siteIds, userIds, searchText } = filters;
  const isLoading = useActivitySelector(selectActivityIsLoading);
  const error = useActivitySelector(selectActivityError);
  const hasLoadedOnce = useActivitySelector(selectHasLoadedOnce);
  const selectedItem = useActivitySelector(selectSelectedActivityItem);
  const columnConfig = useActivitySelector(selectColumnConfig);
  const [hasTriggeredInitialFetch, setHasTriggeredInitialFetch] = useState(false);

  // Conditional loading state based on flow type:
  // - New flow (landing page): show loader while fetching accessible customers OR query not yet triggered OR query loading
  // - Old flow (org management): show loader only while query loading
  const isLoadingState = isNewFlow
    ? ((Boolean(isSIFlow) && isLoadingAccessibleCustomers) || !hasTriggeredInitialFetch || isLoading)
    : isLoading;

  // True when at least one filter category (status / activity / site / user) is
  // active AND the search box is empty. Search text is intentionally excluded:
  // a search-only empty result should show the generic "No data found" message,
  // not the "filters changed" message with the Reset Filters button.
  const hasActiveFilters =
    (statuses.length > 0 ||
    activityFilters.length > 0 ||
    siteIds.length > 0 ||
    userIds.length > 0) &&
    searchText.trim().length === 0;

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Use fast polling while any item is actively in-progress or queued,
  // so retry status updates reach the UI within seconds instead of minutes.
  const hasActiveItems = items.some(
    item => item.status === WorkflowStatus.InProgress || item.status === WorkflowStatus.Queued,
  );
  const refreshInterval = hasActiveItems ? ACTIVE_REFRESH_INTERVAL_MS : IDLE_REFRESH_INTERVAL_MS;

  // Reset state when the tab mounts or the customer changes
  useEffect(() => {
    dispatch(resetActivity({ isSIFlow: Boolean(isSIFlow) }));
    setHasTriggeredInitialFetch(false);
  }, [dispatch, customerId, isSIFlow]);

  // Auto-refresh — interval shrinks to 10s while items are in-flight, 5min otherwise
  useEffect(() => {
    // Only start auto-refresh if we have valid customer IDs
    if (customerIds.length === 0) {
      return;
    }

    timerRef.current = setInterval(() => {
      dispatch(fetchActivity({ customerIds, siteIds: requestSiteIds }));
    }, refreshInterval);

    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
    };
  }, [dispatch, customerIds, requestSiteIds, refreshInterval]);

  // Initial fetch + re-fetch whenever filters, pagination or sort change
  useEffect(() => {
    // Only fetch if we have valid customer IDs
    if (customerIds.length === 0) {
      return;
    }
    setHasTriggeredInitialFetch(true);
    dispatch(fetchActivity({ customerIds, siteIds: requestSiteIds }));
  }, [dispatch, customerIds, requestSiteIds, filters, currentPage, pageSize, sort]);

  const handleSearchChange = useCallback(
    (searchText: string) => {
      dispatch(setFilters({ searchText }));
    },
    [dispatch],
  );

  const handleFilterApply = useCallback(
    ({ statuses, activity, siteIds, userIds }: IFilterSelections) => {
      dispatch(setFilters({ statuses, activity, siteIds, userIds }));
    },
    [dispatch],
  );

  const handleColumnApply = useCallback(
    (config: IColumnConfig) => {
      dispatch(setColumnConfig(config));
    },
    [dispatch],
  );

  const handleSort = useCallback(
    (sortBy: SortBy, sortOrder: SortOrder) => {
      dispatch(setSort({ sortBy, sortOrder }));
    },
    [dispatch],
  );

  const handleRowClick = useCallback(
    (item: IActivityItem) => {
      dispatch(setSelectedItem(item));
    },
    [dispatch],
  );

  const handleModalClose = useCallback(() => {
    dispatch(clearSelectedItem());
  }, [dispatch]);

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(setPage(page));
    },
    [dispatch],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      dispatch(setPageSize(pageSize));
    },
    [dispatch],
  );

  const handleRefresh = useCallback(() => {
    if (customerIds.length === 0) {
      return;
    }
    dispatch(fetchActivity({ customerIds, siteIds: requestSiteIds }));
  }, [dispatch, customerIds, requestSiteIds]);

  const handleResetFilters = useCallback(() => {
    dispatch(resetFilters());
  }, [dispatch]);

  return (
    <Card className="activity-card">
      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-py-4">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-pb-4">
          <h2 className="tw-text-lg tw-font-semibold">{activityTableTitle || t('activityLogTitle')}</h2>
          {!error && hasLoadedOnce && (
            <ActivityToolbar
              onSearchChange={handleSearchChange}
              customerIds={filterCustomerIds}
              siOrgId={siOrgId}
              siteIds={selectedSiteId ? [selectedSiteId] : undefined}
              onFilterApply={handleFilterApply}
              columnConfig={columnConfig}
              onColumnApply={handleColumnApply}
            />
          )}
        </div>

        {error ? (
          <ActivityErrorState onRefresh={handleRefresh} message={t('activityLogFetchError')} />
        ) : (
          <ActivityTable
            items={items}
            sort={sort}
            pagination={pagination}
            isLoading={isLoadingState}
            isInitialLoad={!hasLoadedOnce}
            columnConfig={columnConfig}
            hasActiveFilters={hasActiveFilters}
            onSort={handleSort}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onResetFilters={handleResetFilters}
          />
        )}

        {selectedItem && (
          <ActivityDetailModal item={selectedItem} onClose={handleModalClose} />
        )}
      </div>
    </Card>
  );
};

const ActivityTabContainerWithToast: React.FC<ActivityTabContainerProps> = (props) => (
  <I18nextProvider i18n={i18next}>
    <ToastProvider>
      <ActivityToastBridge />
      <ActivityTabContainer {...props} />
      <ToastContainer />
    </ToastProvider>
  </I18nextProvider>
);

export default ActivityTabContainerWithToast;
